'use client'

/**
 * PrereqChecklist — pre-tour prerequisite gating UI.
 *
 * Surfaces a tour's preconditions up-front as a checklist the user
 * self-confirms. Self-declaration is intentional — we do NOT query the
 * database. When `tour.prerequisites` is absent or empty, this component
 * never renders and the tour starts immediately.
 */

import { useState, useMemo } from 'react'

export function PrereqChecklist({ tour, onConfirm, onCancel, onTakeMeThere, initialTicks, theme }) {
  const prereqs = useMemo(
    () => Array.isArray(tour?.prerequisites) ? tour.prerequisites : [],
    [tour]
  )
  const [checked, setChecked] = useState(() => {
    if (Array.isArray(initialTicks) && initialTicks.length === prereqs.length) {
      return initialTicks.map(Boolean)
    }
    return prereqs.map(() => false)
  })

  if (!tour || prereqs.length === 0) return null

  const isRequired = (p) => p?.required !== false
  const requiredIdx = prereqs.map((p, i) => isRequired(p) ? i : -1).filter(i => i >= 0)
  const unlockIdx = prereqs.map((p, i) => isRequired(p) ? -1 : i).filter(i => i >= 0)

  const allRequiredTicked = requiredIdx.every((i) => checked[i])

  const toggle = (i) => {
    setChecked(prev => prev.map((v, idx) => (idx === i ? !v : v)))
  }

  const primary = theme?.primary || '#3b82f6'
  const bgPanel = theme?.panelBg || '#ffffff'
  const fg = theme?.fg || '#0f172a'
  const muted = theme?.muted || '#64748b'

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99998,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel?.()
      }}
    >
      <div
        style={{
          background: bgPanel,
          color: fg,
          borderRadius: 12,
          maxWidth: 460,
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          padding: '1.25rem',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: muted,
            textTransform: 'uppercase',
          }}>
            Before we start
          </div>
          <h3 style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 600 }}>
            {tour.title || 'Quick check'}
          </h3>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: muted, lineHeight: 1.5 }}>
            Tick each item once you&apos;ve confirmed it&apos;s in place. We&apos;ll then walk you through the rest.
          </p>
        </div>

        {requiredIdx.length > 0 && (
          <PrereqGroup
            showHeading={unlockIdx.length > 0}
            headingLabel="Required"
            headingColor={primary}
            indexes={requiredIdx}
            prereqs={prereqs}
            checked={checked}
            toggle={toggle}
            onTakeMeThere={onTakeMeThere}
            primary={primary}
            muted={muted}
          />
        )}

        {unlockIdx.length > 0 && (
          <PrereqGroup
            showHeading={true}
            headingLabel="Good to have"
            headingColor={muted}
            indexes={unlockIdx}
            prereqs={prereqs}
            checked={checked}
            toggle={toggle}
            onTakeMeThere={onTakeMeThere}
            primary={primary}
            muted={muted}
          />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button
            type="button"
            onClick={() => onCancel?.()}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: 'transparent',
              color: fg,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.()}
            disabled={!allRequiredTicked}
            style={{
              padding: '8px 14px',
              borderRadius: 6,
              border: 'none',
              background: allRequiredTicked ? primary : '#cbd5e1',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: allRequiredTicked ? 'pointer' : 'not-allowed',
              opacity: allRequiredTicked ? 1 : 0.7,
            }}
            title={allRequiredTicked ? 'Start the tour' : 'Tick all required prerequisites first'}
          >
            I&apos;ve done these — let&apos;s go
          </button>
        </div>
      </div>
    </div>
  )
}

export default PrereqChecklist

function PrereqGroup({
  showHeading,
  headingLabel,
  headingColor,
  indexes,
  prereqs,
  checked,
  toggle,
  onTakeMeThere,
  primary,
  muted,
}) {
  return (
    <div style={{ margin: '14px 0' }}>
      {showHeading && (
        <div style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.05em',
          color: headingColor,
          textTransform: 'uppercase',
          marginBottom: 6,
        }}>
          {headingLabel}
        </div>
      )}
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {indexes.map((i) => {
          const p = prereqs[i]
          return (
            <li
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                border: `1px solid ${checked[i] ? primary : '#e2e8f0'}`,
                borderRadius: 8,
                background: checked[i] ? '#eff6ff' : 'transparent',
                cursor: 'pointer',
                transition: 'background 0.12s, border-color 0.12s',
              }}
              onClick={() => toggle(i)}
            >
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => toggle(i)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  marginTop: 2,
                  width: 16,
                  height: 16,
                  accentColor: primary,
                  cursor: 'pointer',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.4 }}>
                  {p.label}
                </div>
                {p.hint && (
                  <div style={{ fontSize: 11.5, color: muted, marginTop: 2, lineHeight: 1.4 }}>
                    {p.hint}
                  </div>
                )}
                {p.href && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (typeof onTakeMeThere === 'function') {
                        onTakeMeThere({
                          href: p.href,
                          prereqIndex: i,
                          tickedNow: checked,
                        })
                      } else if (typeof window !== 'undefined') {
                        window.location.assign(p.href)
                      }
                    }}
                    style={{
                      fontSize: 11.5,
                      color: primary,
                      textDecoration: 'underline',
                      marginTop: 4,
                      display: 'inline-block',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Take me there →
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
